/**
 * Browser capability tools — generic registry pattern.
 *
 * Each capability is registered with `makeBrowserTool()` — a thin wrapper
 * that fills in the Tool shape (variant, color, blurb, JSON schema) from a
 * minimal capability descriptor. Adding a new browser capability = one call.
 *
 * The LLM sees only the description and parameter schema. The implementation
 * (JS API calls, fetch, permissions) is never exposed. Results flow back
 * through the normal tool_result trace like any other tool.
 *
 * Capabilities:
 *   get_browser_context  — date, time, timezone, language, platform, dark mode
 *   get_location         — navigator.geolocation → lat/lon/accuracy
 *   get_weather          — Open-Meteo (free, no key) → temp, conditions, forecast
 *   get_network_info     — connection type, speed estimate, online status
 *   get_screen_info      — viewport, pixel ratio, orientation, touch capability
 *   get_clipboard        — navigator.clipboard.readText() → pasted content
 */

import type { Tool, ToolContext, ToolResult } from './tools';
import type { BotVariant } from '../Components/Bots/motionSystem';

// ── Palette — one colour per capability domain ─────────────────────────────
const COLORS = {
    time:     '#f1fa8c',  // yellow  — temporal
    location: '#ff79c6',  // pink    — geographic
    weather:  '#8be9fd',  // cyan    — atmospheric
    network:  '#ffb86c',  // orange  — connectivity
    screen:   '#bd93f9',  // purple  — display
    clipboard:'#50fa7b',  // green   — content
};

// ── Registry helper ────────────────────────────────────────────────────────
interface CapabilityDef {
    name: string;
    label: string;
    blurb: string;
    description: string;
    color: string;
    variant: BotVariant;
    parameters?: Record<string, unknown>;
    run: (args: Record<string, any>, ctx: ToolContext) => ToolResult | Promise<ToolResult>;
}

function makeBrowserTool(def: CapabilityDef): Tool {
    return {
        name:        def.name,
        label:       def.label,
        blurb:       def.blurb,
        description: def.description,
        color:       def.color,
        variant:     def.variant,
        parameters:  def.parameters ?? { type: 'object', properties: {}, required: [] },
        run:         def.run,
    };
}

// ── WMO weather code → English ─────────────────────────────────────────────
const WMO: Record<number, string> = {
    0: 'clear sky', 1: 'mainly clear', 2: 'partly cloudy', 3: 'overcast',
    45: 'foggy', 48: 'icy fog',
    51: 'light drizzle', 53: 'drizzle', 55: 'heavy drizzle',
    61: 'light rain', 63: 'rain', 65: 'heavy rain',
    71: 'light snow', 73: 'snow', 75: 'heavy snow', 77: 'snow grains',
    80: 'light showers', 81: 'showers', 82: 'violent showers',
    85: 'snow showers', 86: 'heavy snow showers',
    95: 'thunderstorm', 96: 'thunderstorm with hail', 99: 'thunderstorm with heavy hail',
};

// ── Capabilities ───────────────────────────────────────────────────────────

const browserContext = makeBrowserTool({
    name: 'get_browser_context',
    label: 'context',
    color: COLORS.time,
    variant: 'squircle',
    blurb: 'local date, time, timezone, language, platform',
    description:
        'Read the user\'s browser environment: local date and time, timezone, preferred language, ' +
        'platform (mobile/desktop), and whether they prefer dark mode. ' +
        'Use this when the user asks about the current time, date, day of week, their device, ' +
        'or when a time-aware answer is needed.',
    run: () => {
        const now  = new Date();
        const tz   = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const lang = navigator.language || 'unknown';
        const ua   = navigator.userAgent;
        const platform   = /Mobi|Android|iPhone|iPad/i.test(ua) ? 'mobile' : 'desktop';
        const darkMode   = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? null;
        const timeStr    = now.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
        const dateStr    = now.toLocaleDateString(lang, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        return {
            summary: `browser context: ${dateStr}, ${timeStr}, ${tz}, ${platform}`,
            data: {
                local_date: dateStr, local_time: timeStr, timezone: tz,
                language: lang, platform, dark_mode: darkMode,
                utc_iso: now.toISOString(),
            },
        };
    },
});

const getLocation = makeBrowserTool({
    name: 'get_location',
    label: 'locate',
    color: COLORS.location,
    variant: 'cloud',
    blurb: 'asks browser for GPS coordinates',
    description:
        'Get the user\'s current geographic location using the browser Geolocation API. ' +
        'Use this when the user asks about weather, local time, nearby places, or anything location-dependent. ' +
        'The browser will prompt the user for permission. Call this before get_weather.',
    run: () => {
        if (!navigator.geolocation) {
            return Promise.resolve({
                summary: 'geolocation not available',
                data: { error: 'Geolocation API not supported in this browser' },
            });
        }
        return new Promise<ToolResult>((resolve) => {
            navigator.geolocation.getCurrentPosition(
                ({ coords: { latitude, longitude, accuracy } }) => resolve({
                    summary: `location: lat ${latitude.toFixed(3)}, lon ${longitude.toFixed(3)} (±${Math.round(accuracy)}m)`,
                    data: { latitude, longitude, accuracy_m: Math.round(accuracy) },
                }),
                (err) => {
                    const msg = err.code === 1 ? 'permission denied'
                        : err.code === 2 ? 'position unavailable'
                        : 'request timed out';
                    resolve({ summary: `location failed: ${msg}`, data: { error: msg } });
                },
                { timeout: 8000, maximumAge: 300_000 },
            );
        });
    },
});

const getWeather = makeBrowserTool({
    name: 'get_weather',
    label: 'weather',
    color: COLORS.weather,
    variant: 'cloud',
    blurb: 'current weather + today\'s forecast via Open-Meteo',
    description:
        'Fetch current weather and today\'s forecast for given coordinates. ' +
        'Always call get_location first to get the coordinates. ' +
        'Returns temperature (°C), weather condition, wind speed, humidity, and today\'s high/low. ' +
        'Uses Open-Meteo — free, no API key required.',
    parameters: {
        type: 'object',
        properties: {
            latitude:  { type: 'number', description: 'Latitude from get_location' },
            longitude: { type: 'number', description: 'Longitude from get_location' },
        },
        required: ['latitude', 'longitude'],
    },
    run: async ({ latitude, longitude }) => {
        const lat = Number(latitude), lon = Number(longitude);
        if (isNaN(lat) || isNaN(lon)) {
            return { summary: 'invalid coordinates', data: { error: 'lat/lon must be numbers' } };
        }
        try {
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
                `&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,relative_humidity_2m` +
                `&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weather_code` +
                `&timezone=auto&forecast_days=3`;
            const d = await fetch(url).then(r => r.ok ? r.json() : Promise.reject(r.status));
            const c = d.current;
            const day = d.daily;
            const condition = WMO[c.weather_code] ?? `code ${c.weather_code}`;
            // 3-day forecast
            const forecast = day.time.slice(0, 3).map((date: string, i: number) => ({
                date,
                high_c:  Math.round(day.temperature_2m_max[i]),
                low_c:   Math.round(day.temperature_2m_min[i]),
                rain_mm: Math.round(day.precipitation_sum[i] * 10) / 10,
                condition: WMO[day.weather_code[i]] ?? `code ${day.weather_code[i]}`,
            }));
            return {
                summary: `${Math.round(c.temperature_2m)}°C, ${condition} at (${lat.toFixed(2)}, ${lon.toFixed(2)})`,
                data: {
                    temperature_c:   Math.round(c.temperature_2m),
                    feels_like_c:    Math.round(c.apparent_temperature),
                    condition,
                    wind_kph:        Math.round(c.wind_speed_10m),
                    humidity_pct:    Math.round(c.relative_humidity_2m),
                    timezone:        d.timezone_abbreviation || d.timezone,
                    forecast_3_days: forecast,
                    coordinates:     { lat, lon },
                },
            };
        } catch (err: any) {
            return { summary: `weather fetch failed: ${err}`, data: { error: String(err) } };
        }
    },
});

const getNetworkInfo = makeBrowserTool({
    name: 'get_network_info',
    label: 'network',
    color: COLORS.network,
    variant: 'hexagon',
    blurb: 'connection type, speed estimate, online status',
    description:
        'Read the user\'s network connection status and type. ' +
        'Returns whether they\'re online, connection type (wifi/4g/3g/slow-2g/unknown), ' +
        'estimated downlink speed, and data-saver mode. ' +
        'Use this when the user asks about connectivity, or when deciding whether to suggest ' +
        'bandwidth-heavy features.',
    run: () => {
        const online = navigator.onLine;
        const conn   = (navigator as any).connection
            ?? (navigator as any).mozConnection
            ?? (navigator as any).webkitConnection;
        return {
            summary: online
                ? `online — ${conn?.effectiveType ?? 'connection type unknown'}`
                : 'offline',
            data: {
                online,
                effective_type:   conn?.effectiveType    ?? null,
                downlink_mbps:    conn?.downlink          ?? null,
                rtt_ms:           conn?.rtt               ?? null,
                save_data:        conn?.saveData           ?? null,
                type:             conn?.type               ?? null,
            },
        };
    },
});

const getScreenInfo = makeBrowserTool({
    name: 'get_screen_info',
    label: 'screen',
    color: COLORS.screen,
    variant: 'pill',
    blurb: 'viewport size, pixel ratio, orientation, touch',
    description:
        'Read the user\'s display properties: viewport dimensions, device pixel ratio, ' +
        'screen orientation (portrait/landscape), and whether the device has a touch screen. ' +
        'Use this when the user asks about their screen, or when layout/resolution context matters.',
    run: () => {
        const vw = window.innerWidth, vh = window.innerHeight;
        const sw = window.screen.width, sh = window.screen.height;
        const dpr = window.devicePixelRatio ?? 1;
        const orientation = vw > vh ? 'landscape' : 'portrait';
        const touch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
        return {
            summary: `screen: ${vw}×${vh}px viewport, ${orientation}, dpr ${dpr.toFixed(1)}, ${touch ? 'touch' : 'no touch'}`,
            data: {
                viewport:    { width: vw, height: vh },
                screen:      { width: sw, height: sh },
                pixel_ratio: dpr,
                orientation,
                touch_screen: touch,
                color_depth: window.screen.colorDepth,
            },
        };
    },
});

const getClipboard = makeBrowserTool({
    name: 'get_clipboard',
    label: 'clipboard',
    color: COLORS.clipboard,
    variant: 'teardrop',
    blurb: 'reads text from the clipboard',
    description:
        'Read text the user has copied to their clipboard. ' +
        'Use this when the user says "I copied something", "analyse this", "I have a URL/code/text", ' +
        'or pastes something and wants it processed. ' +
        'The browser will prompt the user for clipboard permission.',
    run: async () => {
        if (!navigator.clipboard?.readText) {
            return {
                summary: 'clipboard not available',
                data: { error: 'Clipboard API not supported — try pasting directly into the chat.' },
            };
        }
        try {
            const text = await navigator.clipboard.readText();
            if (!text) return { summary: 'clipboard is empty', data: { text: '', length: 0 } };
            const preview = text.length > 200 ? text.slice(0, 200) + '…' : text;
            return {
                summary: `clipboard: ${text.length} chars — "${preview}"`,
                data: { text: text.slice(0, 8000), length: text.length },
            };
        } catch (err: any) {
            const msg = err?.name === 'NotAllowedError'
                ? 'permission denied — allow clipboard access and try again'
                : `clipboard read failed: ${err?.message}`;
            return { summary: msg, data: { error: msg } };
        }
    },
});

// ── Exported registry ──────────────────────────────────────────────────────
// Import this array into tools.ts and spread into TOOLS to activate all.

export const BROWSER_TOOLS: Tool[] = [
    browserContext,
    getLocation,
    getWeather,
    getNetworkInfo,
    getScreenInfo,
    getClipboard,
];
